import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useAuth } from "react-oidc-context";
import { CircleAlert, CircleCheck, Loader2, ShieldAlert } from "@fg/icons";
import { checkAll, defaultAnswers, upgradeSchema } from "@fg/web-core/forms";
import type { FormSchema } from "@fg/web-core/forms";
import { FormRenderer } from "@fg/web-core/forms/ui";
import { Button } from "@/components/ui/button";
import { returnState } from "@/auth/returnTo";

/**
 * Public form page (/forms/:token): a form published from Workbook, answered here. Outside ProtectedRoute, so a link set to
 * "anyone" opens with no sign-in; a link set to signed-in people or a list asks the visitor to sign in first.
 * The form itself (every control type, style preset, colour group, comb field ...) is drawn by the shared FormRenderer; the same
 * rules (validation, show-when) run here as the user types and again on the server.
 */
interface PublicForm { title: string; description: string | null; schema?: unknown; questions?: unknown; accessMode: string; signedInAs: string | null; oneResponse: boolean; confirmation: string | null }
type Page =
    | { kind: "loading" }
    | { kind: "form"; form: PublicForm; schema: FormSchema }
    | { kind: "signin"; title: string; message: string }
    | { kind: "denied"; title: string; message: string; as: string | null }
    | { kind: "closed"; title: string; message: string }
    | { kind: "missing" }
    | { kind: "offline" }
    | { kind: "done"; form: PublicForm; schema: FormSchema; message: string };

const baseUrl = ((import.meta.env as Record<string, string | undefined>).VITE_DRIVE_BFF_BASE_URL ?? "").replace(/\/+$/, "");
const enc = encodeURIComponent;

/** Fresh answers: each field's default, and a hidden field takes ?its-id=value from the link (for prefilled links). */
function startAnswers(schema: FormSchema): string[] {
    const params = new URLSearchParams(window.location.search);
    return defaultAnswers(schema.fields).map((d, i) => {
        const f = schema.fields[i]!;
        return f.type === "hidden" ? (params.get(f.id) ?? d).slice(0, 500) : d;
    });
}

export function FormPage() {
    const { token } = useParams<{ token: string }>();
    const auth = useAuth();
    const access = auth.user?.access_token;
    const [page, setPage] = useState<Page>({ kind: "loading" });
    const [answers, setAnswers] = useState<string[]>([]);
    const [tried, setTried] = useState(false);
    const [busy, setBusy] = useState(false);
    const [problem, setProblem] = useState<string | null>(null);

    useEffect(() => {
        if (auth.isLoading || !token) return;
        let alive = true;
        void (async () => {
            let res: Response;
            try {
                res = await fetch(`${baseUrl}/api/forms/public/${enc(token)}`, { headers: { Accept: "application/json", ...(access ? { Authorization: `Bearer ${access}` } : {}) }, credentials: "omit" });
            } catch { if (alive) setPage({ kind: "offline" }); return; }
            const body = (await res.json().catch(() => ({}))) as Partial<PublicForm> & { title?: string; error?: string; signedInAs?: string };
            if (!alive) return;
            if (res.ok) {
                const f = body as PublicForm;
                // older servers sent a bare questions array; both shapes load
                const schema = upgradeSchema(f.schema ?? f.questions);
                setPage({ kind: "form", form: f, schema });
                setAnswers(startAnswers(schema));
                document.title = f.title;
            }
            else if (res.status === 401) setPage({ kind: "signin", title: body.title ?? "Form", message: body.error ?? "Sign in to respond to this form." });
            else if (res.status === 403) setPage({ kind: "denied", title: body.title ?? "Form", message: body.error ?? "You cannot respond to this form.", as: body.signedInAs ?? null });
            else if (res.status === 410) setPage({ kind: "closed", title: body.title ?? "Form", message: body.error ?? "This form is closed." });
            else setPage({ kind: "missing" });
        })();
        return () => { alive = false; };
    }, [auth.isLoading, token, access]);

    const schemaNow = page.kind === "form" ? page.schema : null;
    // Problems show after the first Submit and then follow the answers as they are fixed.
    const checked = useMemo(() => (schemaNow ? checkAll(schemaNow, answers) : null), [schemaNow, answers]);
    const errors = tried && checked ? checked.errors : [];

    const submit = async (form: PublicForm, schema: FormSchema) => {
        setTried(true);
        setProblem(null);
        const r = checkAll(schema, answers);
        const first = r.errors.findIndex(Boolean);
        if (first >= 0) { document.querySelector(`[data-question="${first}"]`)?.scrollIntoView({ behavior: "smooth", block: "center" }); return; }
        setBusy(true);
        try {
            const res = await fetch(`${baseUrl}/api/forms/public/${enc(token!)}/responses`, {
                method: "POST",
                headers: { "Content-Type": "application/json", Accept: "application/json", ...(access ? { Authorization: `Bearer ${access}` } : {}) },
                credentials: "omit",
                body: JSON.stringify({ answers: r.cleaned })
            });
            const body = (await res.json().catch(() => ({}))) as { error?: string; message?: string };
            if (res.ok) setPage({ kind: "done", form, schema, message: body.message ?? "Your response has been recorded." });
            else setProblem(body.error ?? `Could not send the response (${res.status}).`);
        } catch { setProblem("The form could not be sent. Check your connection and try again."); }
        finally { setBusy(false); }
    };

    const shell = (children: React.ReactNode) => (
        <div className="min-h-screen bg-muted/40 px-4 py-8">
            <div className="mx-auto max-w-xl space-y-4">{children}</div>
        </div>
    );
    const card = "rounded-xl border bg-card p-6 shadow-sm";

    if (page.kind === "loading") return shell(<div className={`${card} flex items-center gap-2 text-sm text-muted-foreground`}><Loader2 className="h-4 w-4 animate-spin" /> Opening the form…</div>);
    if (page.kind === "missing") return shell(<div className={card}><h1 className="text-lg font-semibold">Form not found</h1><p className="mt-1 text-sm text-muted-foreground">This link is not valid. Ask the person who shared it for a new one.</p></div>);
    if (page.kind === "offline") return shell(<div className={card}><h1 className="text-lg font-semibold">Can’t reach the server</h1><p className="mt-1 text-sm text-muted-foreground">Check your connection and reload.</p></div>);
    if (page.kind === "closed") return shell(<div className={card}><h1 className="text-lg font-semibold">{page.title}</h1><p className="mt-2 flex items-center gap-2 text-sm"><CircleAlert className="h-4 w-4" /> {page.message}</p></div>);
    if (page.kind === "signin") {
        return shell(
            <div className={card}>
                <h1 className="text-lg font-semibold">{page.title}</h1>
                <p className="mt-2 flex items-center gap-2 text-sm"><ShieldAlert className="h-4 w-4" /> {page.message}</p>
                <Button className="mt-4" data-form-signin onClick={() => void auth.signinRedirect({ state: returnState() })}>Sign in</Button>
            </div>
        );
    }
    if (page.kind === "denied") {
        return shell(
            <div className={card}>
                <h1 className="text-lg font-semibold">{page.title}</h1>
                <p className="mt-2 flex items-center gap-2 text-sm"><ShieldAlert className="h-4 w-4" /> {page.message}</p>
                {page.as ? <p className="mt-1 text-xs text-muted-foreground">Signed in as {page.as}. This form is limited to certain people or domains.</p> : null}
                <Button variant="outline" className="mt-4" onClick={() => void auth.signoutRedirect({ state: returnState() })}>Use another account</Button>
            </div>
        );
    }
    if (page.kind === "done") {
        return shell(
            <div className={card} data-form-done>
                <h1 className="text-lg font-semibold">{page.form.title}</h1>
                <p className="mt-2 flex items-center gap-2 text-sm"><CircleCheck className="h-4 w-4 text-green-600" /> {page.message}</p>
                {!page.form.oneResponse ? <Button variant="outline" className="mt-4" onClick={() => { setAnswers(startAnswers(page.schema)); setTried(false); setPage({ kind: "form", form: page.form, schema: page.schema }); }}>Submit another response</Button> : null}
            </div>
        );
    }

    const { form, schema } = page;
    return (
        <FormRenderer
            schema={schema} title={form.title} description={form.description} answers={answers} errors={errors} busy={busy} problem={problem}
            notice={form.signedInAs ? <>Responding as {form.signedInAs}. Your email is recorded with your answers.</> : null}
            onChange={(i, v) => setAnswers((a) => a.map((x, k) => (k === i ? v : x)))}
            onReset={() => { setAnswers(startAnswers(schema)); setTried(false); setProblem(null); }}
            onSubmit={() => void submit(form, schema)}
        />
    );
}
